import type { ProductInformation } from '../types/PDPTypes';

export type PdpClientData = {
  dealerName: string;
  city: string;
};

export const DEFAULT_PDP_TITLE_TEMPLATE =
  '{productName} {colorName} by {brandName} in {city} - {dealerName}';

/**
 * Interpolates the PDP title template with product + dealer values. The
 * template is i18n-driven (`messages.products.pdpTitle` on both server and
 * client); pass the resolved string and this does the substitution.
 */
export function buildPdpTitle(
  productInfo: Pick<
    ProductInformation,
    'productName' | 'colorName' | 'brandName'
  >,
  { dealerName, city }: PdpClientData,
  template: string = DEFAULT_PDP_TITLE_TEMPLATE
): string {
  return template
    .replace('{productName}', productInfo.productName ?? '')
    .replace('{colorName}', productInfo.colorName ?? '')
    .replace('{brandName}', productInfo.brandName ?? '')
    .replace('{city}', city ?? '')
    .replace('{dealerName}', dealerName ?? '');
}

export function buildPdpDescription(
  productInfo: Pick<
    ProductInformation,
    'brandName' | 'productType' | 'productName' | 'colorName' | 'specs'
  >,
  { dealerName, city }: PdpClientData
): string {
  const productTypeLower = productInfo.productType?.toLowerCase() ?? '';
  const productTypeLabel =
    productTypeLower === 'carpet' || productTypeLower === 'waterproof flooring'
      ? productInfo.productType
      : `${productInfo.productType} Flooring`;

  let description = `${dealerName} in ${city ?? ''} has a top selection of ${
    productInfo.brandName
  } ${productTypeLabel}, including ${productInfo.productName} ${
    productInfo.colorName
  }`;

  const filters: string[] = [];
  const length = getSpecValue(productInfo, 'Length');
  const width = getSpecValue(productInfo, 'Width');
  if (length) filters.push(length);
  if (width) filters.push(width);

  if (filters.length > 0) {
    description = `${description} in ${filters.join(' ')}`;
  }

  return description;
}

export function buildPdpKeywords(
  productInfo: Pick<
    ProductInformation,
    'brandName' | 'productType' | 'productName' | 'colorName' | 'specs'
  >,
  { dealerName, city }: PdpClientData
): string {
  const keywords: string[] = [
    dealerName,
    city,
    productInfo.productType,
    productInfo.brandName,
    productInfo.productName,
  ];

  if (productInfo.colorName) {
    keywords.push(productInfo.colorName.trim().replaceAll('|', ','));
  }

  const length = getSpecValue(productInfo, 'Length');
  const width = getSpecValue(productInfo, 'Width');
  if (length) keywords.push(length);
  if (width) keywords.push(width);

  return keywords.filter(Boolean).join(', ');
}

function getSpecValue(
  productInfo: Pick<ProductInformation, 'specs'>,
  key: 'Length' | 'Width'
): string {
  const value = productInfo.specs?.find((s) => s.key === key)?.value;
  if (!value) return '';
  return value.trim().replaceAll('|', '');
}

/**
 * The same precedence the server-side metadata uses: companyname token with
 * fallback to the stored ClientName, and the first non-empty city token.
 */
export function extractPdpClientData({
  tokens,
  fallbackClientName,
}: {
  tokens: ReadonlyArray<{
    TokenName: string | undefined;
    Replacement: string | undefined;
  }>;
  fallbackClientName?: string;
}): PdpClientData {
  const get = (name: string) =>
    tokens.find((t) => t.TokenName === name)?.Replacement ?? '';
  return {
    dealerName: get('companyname') || fallbackClientName || '',
    city: get('PrimaryMarket') || get('all-showrooms') || get('city1') || '',
  };
}
